import { useState } from "react"
import { useTranslation } from "react-i18next"
import { format } from "date-fns"
import { Popover } from "@mui/material"
import { RiArrowDropDownFill } from "@remixicon/react"
import BasicButton from "@/components/button/BasicButton"
import Calendar from "@/components/form/calendar/Calendar"
import Icon from "@/components/icon/Icon"
import { createActivityCommonStore } from "@/app/activity/store"
import ActivityViewToggle from "./ActivityViewToggle"
import ActivitySearch from "./ActivitySearch"
import ActivityFilterPopover from "./ActivityFilterPopover"
import "./activityHeader.scss"

interface ActivityHeaderProps {
  onNewActivity: () => void
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  onJumpToDate: (date: Date) => void
}

export default function ActivityHeader({
  onNewActivity,
  onPrev,
  onNext,
  onToday,
  onJumpToDate,
}: ActivityHeaderProps) {
  const { t } = useTranslation()
  const { currentDate, viewMode } = createActivityCommonStore((state) => ({
    currentDate: state.currentDate,
    viewMode: state.viewMode,
  }))

  const [periodAnchorEl, setPeriodAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [isSearchMode, setIsSearchMode] = useState(false)
  const periodLabel = formatPeriodLabel(currentDate, viewMode)

  const handlePeriodClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setPeriodAnchorEl(e.currentTarget)
  }

  const handleDateChange = (value: Date | null) => {
    if (!value) {
      return
    }
    onJumpToDate(value)
    setPeriodAnchorEl(null)
  }

  return (
    <div className="ActivityHeader">
      {isSearchMode ? (
        <ActivitySearch isOpen onClose={() => setIsSearchMode(false)} />
      ) : (
        <div className="ActivityHeader__left">
          <BasicButton size="md" type="primary-contained" onClick={onNewActivity}>
            <Icon type="add" size={20} style={{ opacity: 0.7 }} />
            {t("button.newActivity")}
          </BasicButton>
          <BasicButton size="md" type="default-outlined" onClick={onToday}>
            {t("today")}
          </BasicButton>
          <button className="ActivityHeader__nav-btn" onClick={onPrev} aria-label="Previous period">
            <Icon type="arrow-left" />
          </button>
          <button className="ActivityHeader__nav-btn" onClick={onNext} aria-label="Next period">
            <Icon type="arrow-right" />
          </button>
          <button
            className="ActivityHeader__period-btn"
            onClick={handlePeriodClick}
            aria-label="Jump to month">
            <span className="ActivityHeader__period">{periodLabel}</span>
            <RiArrowDropDownFill size={20} />
          </button>
          <Popover
            open={!!periodAnchorEl}
            anchorEl={periodAnchorEl}
            onClose={() => setPeriodAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}>
            {periodAnchorEl && (
              <div className="ActivityHeader__period-popover">
                <Calendar
                  value={currentDate}
                  views={["year", "month"]}
                  openTo="month"
                  onChange={handleDateChange}
                />
              </div>
            )}
          </Popover>
        </div>
      )}
      <div className="ActivityHeader__right">
        {!isSearchMode && <ActivitySearch isOpen={false} onOpen={() => setIsSearchMode(true)} />}
        <ActivityFilterPopover />
        <ActivityViewToggle />
      </div>
    </div>
  )
}

function formatPeriodLabel(date: Date, viewMode: "month" | "week" | "schedule") {
  if (viewMode === "month" || viewMode === "schedule") {
    return format(date, "MMMM yyyy")
  }

  return format(date, "MMMM yyyy")
}
